import './CronogramaVisual.css';

interface CronogramaVisualProps {
  dataInicio: string;
  dataConclusao: string;
  progressoFisico: number;
}

function calcularProgressoTemporal(dataInicio: string, dataConclusao: string): number {
  const inicio = new Date(dataInicio).getTime();
  const conclusao = new Date(dataConclusao).getTime();
  const agora = Date.now();

  if (agora <= inicio) return 0;
  if (agora >= conclusao) return 100;

  const totalDuracao = conclusao - inicio;
  const decorrido = agora - inicio;
  return Math.round((decorrido / totalDuracao) * 100);
}

function formatarData(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getStatusInfo(temporal: number, fisico: number) {
  const diferenca = temporal - fisico;

  if (diferenca > 15) {
    return {
      label: '⚠️ Projeto atrasado',
      className: 'atrasado',
      description: `O progresso físico está ${diferenca}% abaixo do esperado pelo tempo decorrido.`,
    };
  }

  if (diferenca < -10) {
    return {
      label: '🚀 Projeto adiantado',
      className: 'adiantado',
      description: `O progresso físico está ${Math.abs(diferenca)}% acima do esperado.`,
    };
  }

  return {
    label: '✅ Projeto em dia',
    className: 'em-dia',
    description: 'O progresso físico está alinhado com o cronograma.',
  };
}

function CronogramaVisual({ dataInicio, dataConclusao, progressoFisico }: CronogramaVisualProps) {
  const progressoTemporal = calcularProgressoTemporal(dataInicio, dataConclusao);
  const clampedFisico = Math.min(100, Math.max(0, Math.round(progressoFisico)));
  const status = getStatusInfo(progressoTemporal, clampedFisico);

  return (
    <div className="cronograma-visual" aria-label="Cronograma do projeto">
      <h2>Cronograma</h2>

      <div className="cronograma-bars">
        <div className="cronograma-bar-section">
          <div className="cronograma-bar-label">
            <span>Tempo Decorrido</span>
            <span className="cronograma-bar-value">{progressoTemporal}%</span>
          </div>
          <div
            className="cronograma-bar-track"
            role="progressbar"
            aria-valuenow={progressoTemporal}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Progresso temporal"
          >
            <div
              className="cronograma-bar-fill temporal"
              style={{ width: `${progressoTemporal}%` }}
            />
          </div>
        </div>

        <div className="cronograma-bar-section">
          <div className="cronograma-bar-label">
            <span>Progresso Físico</span>
            <span className="cronograma-bar-value">{clampedFisico}%</span>
          </div>
          <div
            className="cronograma-bar-track"
            role="progressbar"
            aria-valuenow={clampedFisico}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Progresso físico"
          >
            <div
              className="cronograma-bar-fill fisico"
              style={{ width: `${clampedFisico}%` }}
            />
          </div>
        </div>
      </div>

      <div className={`cronograma-status ${status.className}`}>
        <span>{status.label}</span>
        <span>{status.description}</span>
      </div>

      <div className="cronograma-dates">
        <span>Início: {formatarData(dataInicio)}</span>
        <span>Previsão: {formatarData(dataConclusao)}</span>
      </div>
    </div>
  );
}

export default CronogramaVisual;
